import { ComponentType, IConfig } from '../../types';
import {
  BASIC_JSON_LOGIC_TOOLTIP,
  BASIC_SWITCH_VALUE_TOOLTIP,
  BASIC_TANSLATE_TOOLTIP,
  BASIC_VALUE_TOOLTIP,
  BASIC_VARIABLE_TOOLTIP,
  ComponentConfigType,
} from '../../utils';

const VVJ = [
  ComponentConfigType.Variable,
  ComponentConfigType.Value,
  ComponentConfigType.JsonLogic,
];
const VVJ_TOOLTIPS = [
  { key: ComponentConfigType.Variable, value: BASIC_VARIABLE_TOOLTIP },
  { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
  { key: ComponentConfigType.JsonLogic, value: BASIC_JSON_LOGIC_TOOLTIP },
];
const SWITCH_TOOLTIPS = [
  { key: ComponentConfigType.Variable, value: BASIC_VARIABLE_TOOLTIP },
  { key: ComponentConfigType.Value, value: BASIC_SWITCH_VALUE_TOOLTIP },
  { key: ComponentConfigType.JsonLogic, value: BASIC_JSON_LOGIC_TOOLTIP },
];

// rootNodes: each row is one top-level folder in the Explorer tree, built via
// CompareWorkspaceRootNodesConfigPanel — explains what a "row" produces and
// what the JsonLogic escape hatch must return, since the generic BASIC_
// tooltips don't mention folders/selectors at all.
const ROOT_NODES_VALUE_TOOLTIP = `
  <div>
    <p>
      Each row you add here becomes one <b>top-level folder</b> in the Compare Workspace Explorer.
    </p>
    <p>
      Set <b>Selector</b> to a <b>JMESPath</b> expression, evaluated against the page's bound data,
      that returns that folder's children (files or nested folders). <code>Counter</code>, <code>Tags</code>
      and <code>Info</code> are optional — a children-count badge, one or more colored labels (use <b>Add tag</b> for
      each extra one), and an ⓘ hover hint on the folder itself.
    </p>
    <p>
      Children returned by <b>Selector</b> can carry their own <code>tags</code> (an array of <code>{ label, tone }</code>),
      <code>counter</code> and <code>info</code> too — put those fields directly on each item in the bound
      data and they show up on that file/folder row, independently of the root folder's own tags.
      A tag can also set <code>color</code> (e.g. <code>"#722ed1"</code> or <code>"purple"</code>), which overrides its <code>tone</code>.
      A single <code>tag</code> object still works as well.
    </p>
    <div style="margin-top: 8px;">
      <b>Example:</b>
      <p style="margin: 4px 0;">
        Bound data:
        <code>{ "attachments": [{ "file": { "id": "f1", "name": "invoice.pdf", "tags": [{ "label": "Signed", "tone": "success" }, { "label": "EU", "color": "#722ed1" }], "counter": "New" } }] }</code>
      </p>
      <ul style="padding-left: 18px; margin-bottom: 0;">
        <li><b>Name:</b> <code>Documentations</code></li>
        <li><b>Selector:</b> <code>@.attachments[].file</code></li>
        <li><b>Counter:</b> on</li>
        <li><b>Tags:</b> label <code>New</code>, tone <code>info</code> (add more with <b>Add tag</b>)</li>
      </ul>
      <p style="margin: 4px 0 0;">
        Produces a <b>"Documentations"</b> folder (blue "New" tag, count badge from its children) containing
        <code>invoice.pdf</code>, which itself shows a green "Signed" and a purple "EU" tag plus a "New" counter — all
        coming straight from that file's own <code>tags</code>/<code>counter</code> fields in the bound data, not
        from the folder's config.
      </p>
    </div>
  </div>
`;

const ROOT_NODES_JSON_LOGIC_TOOLTIP = `
  <div>
    <p>
      Use <b>JSON Logic</b> to compute the whole set of root folders dynamically, instead of
      adding rows manually one by one.
    </p>
    <p>
      The expression must return an <b>array</b> of root node objects
      (<code>name</code>, <code>selector</code>, and optionally <code>counter</code>, <code>tags</code> (an array of <code>{ label, tone, color }</code>), <code>info</code>).
    </p>
  </div>
`;

export const config: IConfig = {
  component: ComponentType.CompareWorkspace,
  configGroups: [
    {
      title: 'Component Properties',
      items: [
        {
          name: 'visible',
          label: 'Visible',
          valueComponent: { type: 'Switch' },
          configTypes: VVJ,
          tooltips: SWITCH_TOOLTIPS,
        },
        {
          name: 'rootNodes',
          label: 'Nodes',
          valueComponent: {
            type: 'CompareWorkspaceRootNodesConfigPanel',
            props: { name: 'rootNodes' },
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: ROOT_NODES_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: ROOT_NODES_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'modelInstanceId',
          label: 'Model Instance ID',
          valueComponent: { type: 'Input' },
          configTypes: VVJ,
          tooltips: VVJ_TOOLTIPS,
        },
        {
          name: 'height',
          label: 'Height',
          valueComponent: { type: 'Input' },
          configTypes: VVJ,
          tooltips: VVJ_TOOLTIPS,
        },
        {
          name: 'explorerDefaultExpanded',
          label: 'Explorer Default Expanded',
          valueComponent: { type: 'Switch' },
          configTypes: VVJ,
          tooltips: SWITCH_TOOLTIPS,
        },
        {
          name: 'tagFilterEnabled',
          label: 'Tag Filter Enabled',
          valueComponent: { type: 'Switch' },
          configTypes: VVJ,
          tooltips: SWITCH_TOOLTIPS,
        },
        {
          name: 'tagFilterShowMoreText',
          label: 'Tag Filter Show More Text',
          valueComponent: { type: 'Input' },
          configTypes: VVJ,
          tooltips: VVJ_TOOLTIPS,
        },
        {
          name: 'tagFilterShowLessText',
          label: 'Tag Filter Show Less Text',
          valueComponent: { type: 'Input' },
          configTypes: VVJ,
          tooltips: VVJ_TOOLTIPS,
        },
        // {
        //   name: 'enableDiff',
        //   label: 'Enable Diff',
        //   valueComponent: { type: 'Switch' },
        //   configTypes: VVJ,
        //   tooltips: SWITCH_TOOLTIPS,
        // },
        // {
        //   name: 'enableComments',
        //   label: 'Enable Comments',
        //   valueComponent: { type: 'Switch' },
        //   configTypes: VVJ,
        //   tooltips: SWITCH_TOOLTIPS,
        // },
        // {
        //   name: 'enableSummary',
        //   label: 'Enable Summary',
        //   valueComponent: { type: 'Switch' },
        //   configTypes: VVJ,
        //   tooltips: SWITCH_TOOLTIPS,
        // },
        // {
        //   name: 'defaultView',
        //   label: 'Default View',
        //   valueComponent: {
        //     type: 'Select',
        //     options: [
        //       { label: 'Redline', value: 'redline' },
        //       { label: 'Text', value: 'text' },
        //     ],
        //   },
        //   configTypes: VVJ,
        //   tooltips: VVJ_TOOLTIPS,
        // },
        {
          name: 'displayMode',
          label: 'Display Mode',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Full-size modal (button opens it)', value: 'modal' },
              { label: 'Inline', value: 'inline' },
            ],
          },
          configTypes: VVJ,
          tooltips: VVJ_TOOLTIPS,
        },
        {
          name: 'modalButtonText',
          label: 'Modal Button Text',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
            ComponentConfigType.Translate,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Variable,
              value: BASIC_VARIABLE_TOOLTIP,
            },
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
            {
              key: ComponentConfigType.Translate,
              value: BASIC_TANSLATE_TOOLTIP,
            },
          ],
        },
        {
          name: 'modalButtonType',
          label: 'Modal Button Type',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Primary', value: 'primary' },
              { label: 'Default', value: 'default' },
              { label: 'Dashed', value: 'dashed' },
              { label: 'Text', value: 'text' },
              { label: 'Link', value: 'link' },
            ],
          },
          configTypes: VVJ,
          tooltips: VVJ_TOOLTIPS,
        },
      ],
    },
    {
      title: 'Custom Panels',
      component: 'CompareWorkspaceCustomPanelsConfigPanel',
    },
    {
      title: 'Component Styles',
      component: 'CssSettings',
    },
  ],
};
